/**
 * O que este arquivo lê de um erro do axios. Descrito aqui, e não importado do
 * axios — nem como tipo: o arquivo sai pelo índice do pacote, que a API e o
 * site também importam, e para eles o axios é peer opcional que as imagens não
 * instalam. O import de valor derrubava o boot da API ("Cannot find module
 * 'axios'"); o `import type` derrubava o `next build` do site, que checa os
 * tipos dos arquivos do pacote que importa.
 */
interface AxiosLikeError {
  isAxiosError: true;
  code?: string;
  response?: { status: number; data?: unknown };
}

/** O mesmo teste do `isAxiosError` do axios: `payload.isAxiosError === true`. */
function isAxiosError(err: unknown): err is AxiosLikeError {
  return typeof err === 'object' && err !== null && (err as { isAxiosError?: unknown }).isAxiosError === true;
}

interface ZodValidationIssue {
  message?: string;
  path?: (string | number)[];
}

interface ApiErrorEnvelope {
  error?: string;
  message?: string;
  statusCode?: number;
  validation?: ZodValidationIssue[];
}

/**
 * Extract a user-facing message from an Axios error, matching the API envelope
 * shape from apps/api/src/app.ts setErrorHandler:
 *   { error, message, statusCode, validation? }
 *
 * Falls back through 3 layers:
 *   1. Network-level (no response → "Sem conexão com o servidor.")
 *   2. 4xx/5xx body message (+ zod validation issues if present)
 *   3. Provided fallback (last resort)
 *
 * Used in catch blocks to feed toast({ description: formatApiError(err, '...') })
 * e pelo `ErrorState` do kit web, que é o par visual disto: a mensagem que
 * separa "sem conexão" de "erro do servidor" só serve se alguma tela a mostrar.
 *
 * Vive aqui, ao lado do `createApiClient`, porque é o mesmo envelope de erro que
 * ele produz — e porque os três apps web precisam dela, não só o web-admin.
 */
export function formatApiError(err: unknown, fallback: string): string {
  if (!isAxiosError(err)) return fallback;

  // No response: network/CORS/timeout. Distinct path so user knows it's a
  // connectivity issue, not a backend reject.
  if (!err.response) {
    if (err.code === 'ECONNABORTED') return 'Tempo de resposta esgotado. Tente novamente.';
    return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
  }

  const envelope = err.response.data as ApiErrorEnvelope | undefined;
  const statusCode = err.response.status;

  // 5xx: don't leak server internals to user — generic copy + Sentry has the detail.
  if (statusCode >= 500) {
    return 'Erro no servidor. Tente novamente em instantes.';
  }

  // 4xx: prefer specific message from envelope. Compose with zod validation if available.
  if (envelope?.message) {
    if (envelope.validation && envelope.validation.length > 0) {
      const issues = envelope.validation
        .map((v) => v.message)
        .filter((m): m is string => Boolean(m))
        .slice(0, 3)
        .join('; ');
      if (issues) return `${envelope.message}: ${issues}`;
    }
    return envelope.message;
  }

  return fallback;
}
